import { faCheckCircle } from "@fortawesome/free-solid-svg-icons";
import classNames from "classnames";
import React, { ComponentPropsWithoutRef } from "react";
import { Link } from "react-router-dom";

import Icon from "../Icon";
import { BulletNumber, HorizontalLine, NewStepItem, StepBar, StepContainer, StepItem, StepLabel } from "./Steps.styled";

import { colors } from "src/assets/styles/settings";
import { PropsWithDataQaid } from "src/types/componentProps";

export type StepsProps = ComponentPropsWithoutRef<"div"> &
  PropsWithDataQaid<{
    /**
     * Array of objects of steps
     *
     * What need to be included in object step?
     *  - label (string) -> step label
     *  - status (string) -> step status ('active', 'done', 'none')
     *
     * e.g:
     * {
     *   label: "First Step",
     *   status: "done"
     * },
     * {
     *   label: "Second Step",
     *   status: "active"
     * },
     */
    steps?: { label: string; status: "done" | "active"; url?: string }[];

    /**
     * set the steps to be spreaded full width or shrinked
     */
    isSpreaded?: boolean;

    disabled?: boolean;
    isNewDesign?: boolean;
    hideInactiveLabel?: boolean;
  }>;

const defaultProps: StepsProps = {
  steps: [],
  isSpreaded: true
};

const Steps = React.forwardRef<HTMLDivElement, StepsProps>((props, ref) => {
  const {
    className,
    steps,
    disabled,
    isNewDesign,
    hideInactiveLabel,
    "data-qaid": dataQaid,
    isSpreaded,
    ...restProps
  } = props;
  const stepsClass = classNames("c-steps u-hidden sm:u-flex", className);

  return (
    <StepContainer
      ref={ref}
      className={stepsClass}
      data-qaid={dataQaid}
      {...restProps}
      data-testid="qa-steps-container"
    >
      {steps.map((step, index) => {
        const isLast = index === steps.length - 1;
        const isDone = step.status === "done";
        const isActive = step.status === "active";

        if (isNewDesign) {
          return (
            <NewStepItem
              data-testid="qa-steps-item-new"
              data-qaid={`${dataQaid}-${index + 1}`}
              key={index}
              className={classNames(
                "u-flex u-gap-2 u-items-center",
                !isLast && "u-mr-2",
                isSpreaded && !isLast && "u-flex-1",
                "c-steps__item",
                { "is-done": isDone || isActive },
                { "is-disabled": disabled && isDone }
              )}
            >
              {step.url && isDone ? (
                <Link to={step.url} data-testid="qa-steps-link">
                  <Icon size="1x" icon={faCheckCircle} color={colors.jade} />
                  {!isActive && hideInactiveLabel ? null : (
                    <div
                      data-testid="qa-steps-label"
                      className={classNames(
                        "u-whitespace-nowrap u-font-medium",
                        isActive ? "u-font-bold" : "u-text-neutral"
                      )}
                    >
                      {step.label}
                    </div>
                  )}

                  {!isLast && <HorizontalLine isSpreaded data-testid="qa-steps-bar" />}
                </Link>
              ) : (
                <>
                  <BulletNumber>{index + 1}</BulletNumber>
                  {!isActive && hideInactiveLabel ? null : (
                    <div
                      data-testid="qa-steps-label"
                      className={classNames(
                        "u-whitespace-nowrap u-font-medium",
                        isActive ? "u-font-bold" : "u-text-neutral"
                      )}
                    >
                      {step.label}
                    </div>
                  )}
                  {!isLast && <HorizontalLine isSpreaded data-testid="qa-steps-bar" />}
                </>
              )}
            </NewStepItem>
          );
        }

        return (
          <StepItem
            data-qaid={`${dataQaid}-${index + 1}`}
            key={index}
            className={classNames(
              "c-steps__item",
              { "is-done": isDone || isActive },
              { "is-disabled": disabled && isDone }
            )}
            data-testid="qa-steps-item"
          >
            {step.url && isDone ? (
              <Link to={step.url} data-testid="qa-steps-link">
                <StepLabel data-testid="qa-steps-label">
                  {index + 1}. {step.label}
                </StepLabel>
                <StepBar data-testid="qa-steps-bar" />
              </Link>
            ) : (
              <>
                <StepLabel data-testid="qa-steps-label">
                  {index + 1}. {step.label}
                </StepLabel>
                <StepBar data-testid="qa-steps-bar" />
              </>
            )}
          </StepItem>
        );
      })}
    </StepContainer>
  );
});

Steps.defaultProps = defaultProps;

export default Steps;
